/**
 * "Which site?" — the question an emergency control asks when it does not
 * know the building (FM Manager rebuild B23E). The emergency buttons are never
 * greyed out for want of a building: pressing one with none chosen asks this
 * instead. Used by the FM's header Emergency pop-up (C31) and by the
 * Safety & Emergency page's preset buttons.
 */
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface SiteOption { id: string; name: string }

export function SiteChips({ sites, value, onPick }: {
  sites: SiteOption[]; value: string | null; onPick: (id: string) => void;
}) {
  if (sites.length === 0) {
    return <p className="text-sm text-content-muted" data-testid="which-site-none">You have no sites to choose from.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Which site?" data-testid="which-site">
      {sites.map((s) => (
        <button key={s.id} type="button" aria-pressed={value === s.id} data-testid="site-chip" data-id={s.id}
                onClick={() => onPick(s.id)}
                className={`min-h-[40px] max-w-full break-words rounded-full border px-3 text-left text-sm font-medium ${
                  value === s.id ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-line bg-surface text-content-secondary hover:bg-surface-subtle'}`}>
          {s.name}
        </button>
      ))}
    </div>
  );
}

/** A small dialog asking only the site; `onPick` answers it. */
export function WhichSiteDialog({ open, sites, onPick, onClose, about }: {
  open: boolean; sites: SiteOption[]; onPick: (id: string) => void; onClose: () => void; about?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-surface-inverse/40" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-surface p-5 shadow-xl" data-testid="which-site-dialog">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Dialog.Title as="h2" className="text-base font-semibold text-content">Which site?</Dialog.Title>
                {about && <p className="mt-0.5 text-xs text-content-muted">{about}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close"
                      className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-content-tertiary hover:bg-surface-muted">
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3">
              <SiteChips sites={sites} value={null} onPick={onPick} />
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
